// Purpose: Shared file type detection utilities
// Audience: All components that need to detect or work with file types
// Features:
// - Comprehensive file extension mappings
// - MIME type detection
// - File type classification (video, image, audio, document)
// - Consistent behavior across the application

// File extension to MIME type mappings
export const FILE_EXTENSIONS = {
  // Video formats
  video: {
    mp4: 'video/mp4',
    avi: 'video/avi', 
    mov: 'video/quicktime',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    webm: 'video/webm',
    mkv: 'video/x-matroska',
    m4v: 'video/x-m4v'
  },
  
  // Image formats
  image: {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    webp: 'image/webp',
    svg: 'image/svg+xml'
  },
  
  // Audio formats
  audio: {
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    aac: 'audio/aac',
    ogg: 'audio/ogg',
    m4a: 'audio/mp4',
    flac: 'audio/flac'
  },
  
  // Document formats
  document: {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    txt: 'text/plain',
    rtf: 'application/rtf',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  }
};

// Get file extension from filename
export const getFileExtension = (fileName) => {
  if (!fileName || typeof fileName !== 'string') return null;
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : null;
};

// Get MIME type from filename
export const getContentTypeFromFileName = (fileName) => {
  const ext = getFileExtension(fileName);
  if (!ext) return 'application/octet-stream';
  
  // Check all file type categories
  for (const category of Object.values(FILE_EXTENSIONS)) {
    if (category[ext]) {
      return category[ext];
    }
  }
  
  return 'application/octet-stream';
};

// Check if file is video type
export const isVideoFile = (fileName, contentType) => {
  // Check by explicit content type first
  if (contentType && contentType.startsWith('video/')) {
    return true;
  }
  
  // Check by file extension
  const ext = getFileExtension(fileName);
  return ext && FILE_EXTENSIONS.video[ext] !== undefined;
};

// Check if file is image type
export const isImageFile = (fileName, contentType) => {
  // Check by explicit content type first
  if (contentType && contentType.startsWith('image/')) {
    return true;
  }
  
  // Check by file extension
  const ext = getFileExtension(fileName);
  return ext && FILE_EXTENSIONS.image[ext] !== undefined;
};

// Check if file is audio type
export const isAudioFile = (fileName, contentType) => {
  // Check by explicit content type first
  if (contentType && contentType.startsWith('audio/')) {
    return true;
  }
  
  // Check by file extension
  const ext = getFileExtension(fileName);
  return ext && FILE_EXTENSIONS.audio[ext] !== undefined;
};

// Check if file is document type
export const isDocumentFile = (fileName, contentType) => {
  // Check by file extension
  const ext = getFileExtension(fileName);
  if (ext && FILE_EXTENSIONS.document[ext] !== undefined) {
    return true;
  }
  
  // Check by content type patterns
  if (contentType) {
    return contentType.startsWith('application/') || 
           contentType.startsWith('text/') ||
           contentType === 'application/octet-stream';
  }
  
  return false;
};

// Get file type category
export const getFileTypeCategory = (fileName, contentType) => {
  if (isVideoFile(fileName, contentType)) return 'video';
  if (isImageFile(fileName, contentType)) return 'image';
  if (isAudioFile(fileName, contentType)) return 'audio';
  if (isDocumentFile(fileName, contentType)) return 'document';
  return 'unknown';
};

// Detect file type with multiple fallback methods
export const detectFileType = (evidence) => {
  const fileName = evidence?.fileName || evidence?.name;
  const contentType = evidence?.contentType;
  const explicitType = evidence?.type;
  
  // Priority 1: Explicit type field
  if (explicitType && ['video', 'image', 'audio', 'document'].includes(explicitType)) {
    return explicitType;
  }
  
  // Priority 2: Content type
  if (contentType) {
    if (contentType.startsWith('video/')) return 'video';
    if (contentType.startsWith('image/')) return 'image';
    if (contentType.startsWith('audio/')) return 'audio';
  }
  
  // Priority 3: File extension
  return getFileTypeCategory(fileName, contentType);
};

// Get all supported extensions for a file type
export const getSupportedExtensions = (fileType) => {
  if (!FILE_EXTENSIONS[fileType]) return [];
  return Object.keys(FILE_EXTENSIONS[fileType]);
};

// Check if file type is supported
export const isSupportedFileType = (fileName, contentType) => {
  const category = getFileTypeCategory(fileName, contentType);
  return category !== 'unknown';
};
